import { useState } from 'react';
import type { VcfActivity } from '@/hooks/useWorkstreams';
import type { ResourceRole } from '@/hooks/useAdminRoles';
import { useSkills } from '@/hooks/useSkills';
import { DependencyPicker } from './DependencyPicker';

export function MasterActivityModal({
  activity,
  roles,
  capabilityId,
  siblingActivities,
  onSave,
  onClose,
}: {
  activity: VcfActivity | null;
  roles: ResourceRole[];
  capabilityId: string;
  siblingActivities: Array<{ id: string; name: string }>;
  onSave: (data: { name: string; role: string; baseDays: number; skill?: string; skillId?: string; description?: string; activityPhase?: string; isMilestone?: boolean; customerDays?: number; customerRole?: string }) => void;
  onClose: () => void;
}) {
  const { data: skillsData } = useSkills();
  const skills = skillsData?.skills || [];

  const [name, setName] = useState(activity?.name || '');
  const [role, setRole] = useState(activity?.role || roles[0]?.name || 'Consultant');
  const [baseDays, setBaseDays] = useState(activity ? (activity.baseDays / 100).toString() : '1');
  const [skillId, setSkillId] = useState(activity?.skillId || '');
  const [description, setDescription] = useState(activity?.description || '');
  const [activityPhase, setActivityPhase] = useState(activity?.activityPhase || '');
  const [isMilestone, setIsMilestone] = useState(activity?.isMilestone || false);
  const [customerDays, setCustomerDays] = useState(activity?.customerDays ? (activity.customerDays / 100).toString() : '0');
  const [customerRole, setCustomerRole] = useState(activity?.customerRole || '');

  return (
    <div className="dialog-overlay">
      <div className="dialog max-w-lg">
        <div className="dialog-header">
          <h2 className="dialog-title">{activity ? 'Edit Activity' : 'Add Activity'}</h2>
        </div>
        <div className="dialog-body space-y-5">
          {/* Basic Information */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-3">Basic Information</div>
            <div className="space-y-3">
              <div>
                <label className="label">Name <span className="text-red-400">*</span></label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input mt-1 w-full" placeholder="e.g., Discovery Workshop" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Role <span className="text-red-400">*</span></label>
                  <select value={role} onChange={(e) => setRole(e.target.value)} className="input mt-1 w-full">
                    {roles.map((r) => (
                      <option key={r.id} value={r.name}>{r.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Base Days <span className="text-red-400">*</span></label>
                  <input type="number" value={baseDays} onChange={(e) => setBaseDays(e.target.value)} className="input mt-1 w-full" step="0.5" min="0" required />
                </div>
              </div>
            </div>
          </div>

          {/* Classification */}
          <div className="pt-1 border-t border-gray-100">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-3 mt-3">Classification</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Skill</label>
                <select value={skillId} onChange={(e) => setSkillId(e.target.value)} className="input mt-1 w-full">
                  <option value="">— None —</option>
                  {skills.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Phase</label>
                <select value={activityPhase} onChange={(e) => setActivityPhase(e.target.value)} className="input mt-1 w-full">
                  <option value="">Auto-derive from name</option>
                  <option value="workshop">Workshop</option>
                  <option value="assess">Assess</option>
                  <option value="design">Design</option>
                  <option value="implement">Implement</option>
                  <option value="validate">Validate</option>
                  <option value="document">Document</option>
                  <option value="kt">KT</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <input type="checkbox" id="isMilestone" checked={isMilestone} onChange={(e) => setIsMilestone(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-amber-500" />
              <label htmlFor="isMilestone" className="text-sm text-gray-700">Milestone activity (shown with ◆ icon)</label>
            </div>
          </div>

          {/* Customer */}
          <div className="pt-1 border-t border-gray-100">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-3 mt-3">Customer</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Customer Days</label>
                <input type="number" value={customerDays} onChange={(e) => setCustomerDays(e.target.value)} className="input mt-1 w-full" step="0.5" min="0" placeholder="0" />
              </div>
              <div>
                <label className="label">Customer Role</label>
                <input type="text" value={customerRole} onChange={(e) => setCustomerRole(e.target.value)} className="input mt-1 w-full" placeholder="e.g., Technical Lead" />
              </div>
            </div>
          </div>

          {/* Dependencies */}
          {activity && (
            <div className="pt-1 border-t border-gray-100">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-3 mt-3">Dependencies</div>
              <DependencyPicker
                capabilityId={capabilityId}
                activityId={activity.id}
                siblingActivities={siblingActivities.filter(a => a.id !== activity.id)}
              />
            </div>
          )}

          {/* Notes */}
          <div className="pt-1 border-t border-gray-100">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-3 mt-3">Notes</div>
            <div>
              <label className="label">Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="input mt-1 w-full" rows={2} placeholder="Optional description" />
            </div>
          </div>
        </div>
        <div className="dialog-footer">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={() => {
            const selectedSkill = skills.find(s => s.id === skillId);
            onSave({
              name,
              role,
              baseDays: Math.round(parseFloat(baseDays) * 100),
              skill: selectedSkill?.name || undefined,
              skillId: skillId || undefined,
              description: description || undefined,
              activityPhase: activityPhase || undefined,
              isMilestone,
              customerDays: Math.round(parseFloat(customerDays || '0') * 100),
              customerRole: customerRole || undefined,
            });
          }} className="btn-primary" disabled={!name.trim() || !baseDays}>
            {activity ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
